import { roleAtLeast } from '@os/shared';
import type { SyncProvider } from './provider';

/** What the title pill says about this tab's edits (backlog §19 P16-3.4). */
export type SaveView = 'saved' | 'saving' | 'offline' | 'error' | 'readOnly';

/**
 * P16-3 Rulings 5-6: "Saving…" only after 600 ms of unwritten edits and then for 800 ms at least (no flashing at each
 * keystroke); an error after 15 s online without the room writing anything more of mine (beyond its 10 s maximum
 * `onSave` wait), counted from the latest of: the edits became pending, the connection came back, the room's last
 * progress on them — a long drag or a return from offline is not an error.
 */
export const SAVE_TIMING = { showAfter: 600, minShow: 800, errorAfter: 15_000 } as const;

type Source = Pick<SyncProvider, 'status' | 'role' | 'pendingCount' | 'synced' | 'acks' | 'ownUnsaved' | 'savedAt' | 'progressAt' | 'failure' | 'subscribe' | 'retry'>;

export type SaveState = {
  view(): SaveView;
  /** When everything was last written (the room's ack, or the last "saved" here for an older room). */
  since(): number | null;
  retry(): void;
  subscribe(cb: () => void): () => void;
  destroy(): void;
};

/**
 * The save indicator's state from the real sync state: with a room that acknowledges writes, "saved" means the room
 * wrote this tab's own edits (peers' edits never count); with an older room, online and synced. Subscribers hear
 * view changes only.
 */
export function createSaveState(source: Source, opts: { readOnly?: () => boolean } = {}): SaveState {
  const subs = new Set<() => void>();
  let view: SaveView = 'saved';
  let pendingSince: number | null = null;
  let shownAt: number | null = null;
  let localSavedAt: number | null = null;
  let timedOut = false;
  let onlineSince: number | null = null;
  let lastStatus: SyncProvider['status'] | null = null;
  /** Where the 15 s without progress start. */
  const errorBase = () => Math.max(pendingSince ?? 0, onlineSince ?? 0, source.progressAt ?? 0);
  let timer: ReturnType<typeof setTimeout> | null = null;

  const pending = () =>
    source.acks ? source.ownUnsaved : !(source.status === 'online' && source.synced) && (source.ownUnsaved || source.pendingCount > 0);
  const readOnly = () => source.status === 'revoked' || !!opts.readOnly?.() || (source.role !== null && !roleAtLeast(source.role, 'editor'));

  const update = () => {
    const now = Date.now();
    if (source.status === 'online' && lastStatus !== 'online') onlineSince = now;
    lastStatus = source.status;
    const isPending = pending();
    if (!isPending) {
      pendingSince = null;
      timedOut = false;
    } else {
      pendingSince ??= now;
      if (source.status === 'online' && now - errorBase() >= SAVE_TIMING.errorAfter) timedOut = true;
    }
    let next: SaveView;
    if (source.failure || timedOut) next = 'error';
    else if (readOnly()) next = 'readOnly';
    else if (source.status === 'offline') next = 'offline';
    else if (isPending) next = (view === 'saved' || view === 'readOnly') && now - pendingSince! < SAVE_TIMING.showAfter ? 'saved' : 'saving';
    else next = view === 'saving' && shownAt !== null && now - shownAt < SAVE_TIMING.minShow ? 'saving' : 'saved';
    if (next === 'saving' && view !== 'saving') shownAt = now;
    if (next === 'saved' && view !== 'saved') localSavedAt = now;
    const changed = next !== view;
    view = next;
    schedule(now, isPending);
    if (changed) subs.forEach((cb) => cb());
  };

  const schedule = (now: number, isPending: boolean) => {
    if (timer) clearTimeout(timer);
    timer = null;
    const at: number[] = [];
    if (isPending && pendingSince !== null && !timedOut) {
      if (view !== 'saving') at.push(pendingSince + SAVE_TIMING.showAfter);
      if (source.status === 'online') at.push(errorBase() + SAVE_TIMING.errorAfter);
    }
    if (!isPending && view === 'saving' && shownAt !== null) at.push(shownAt + SAVE_TIMING.minShow);
    const wake = at.filter((t) => t > now);
    if (wake.length) timer = setTimeout(update, Math.min(...wake) - now);
  };

  const off = source.subscribe(update);
  update();

  return {
    view: () => view,
    since: () => (source.acks ? source.savedAt : localSavedAt),
    retry() {
      timedOut = false;
      pendingSince = Date.now();
      source.retry();
      update();
    },
    subscribe(cb) {
      subs.add(cb);
      return () => void subs.delete(cb);
    },
    destroy() {
      off();
      if (timer) clearTimeout(timer);
      subs.clear();
    },
  };
}
